import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AxiosError } from 'axios';
import PhoneInput from '../../UI/PhoneInput';
import Modal from '../../UI/Modal';
import Button from '../../UI/Button';
import Loading from '../../UI/Loading';
import SmsCode from '../../UI/SmsCode';
import bemClassName from '../../utils/bem';
import { RootState } from '../../store';
import './index.scss';
import Validate from '../../API/Validate';
import Service from '../../API/Service';
import getAuthUrl from '../../utils/getAuthUrl';
import { checkAuthStatus } from '../../store/reducers/profile/autorizationStatutsSlice';
import { closeLoginModal } from '../../store/reducers/modal/loginModalSlice';

const loginPanel = bemClassName('login-panel');

const Login = () => {
  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [userExist, setUserExist] = useState<boolean>(false);
  const [userSMS, setUserSMS] = useState<boolean>(false);
  const [loginHint, setLoginHint] = useState<string>();
  const [isLoginLoading, setIsLoginLoading] = useState<boolean>(false);
  const [addUserError, setAddUserError] = useState<string | undefined | null>(null);
  const [codeRepeatHidden, setCodeRepeatHidden] = useState<boolean>(true);
  const [codeIsValid, setCodeIsValid] = useState<boolean>(true);
  const [isCodeInputBlocked, setIsCodeInputBlocked] = useState(false);
  const [codeError, setCodeError] = useState<string>('Неизвестная ошибка');
  const blockTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [blockTimeLeft, setBlockTimeLeft] = useState(0);
  const [authKey, setAuthKey] = useState<string>();
  const [longError, setLongError] = useState<boolean>(false);
  const isOpenLogin = useSelector((state: RootState) => state.loginModal.isOpen);

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const checkPhoneNumber = useCallback(async (phone: string, signal: AbortSignal) => {
    setAddUserError(null);
    if (blockTimerRef.current) clearInterval(blockTimerRef.current);
    if (!phone) return;
    try {
      const responseData = await Validate.checkPhone(phone, signal);
      if (responseData.international) {
        setPhoneIsValid(true);
        setPhoneNumber(responseData.international);
      } else {
        setPhoneIsValid(false);
      }
    } catch (error) {
      const err = error as Error | AxiosError;
      if (err.name !== 'AbortError') {
        setPhoneIsValid(false);
      }
    }
  }, []);

  const checkExist = () => {
    setUserSMS(false);
    setIsLoginLoading(true);
    if (phoneNumber && phoneIsValid && phoneNumber.replace(/\D/g, '').length === 11) {
      Service.checkUserExists(phoneNumber)
        .then((resp) => {
          setUserExist(resp.exist);
          setLoginHint(resp.login_hint);
          setIsLoginLoading(false);
        })
        .catch(() => {
          setIsLoginLoading(false);
        });
    }
    setIsLoginLoading(false);
  };

  const sendCode = () => {
    setIsLoginLoading(true);
    if (loginHint) {
      Service.requestCode(phoneNumber!)
        .then(() => {
          setUserSMS(true);
          setIsLoginLoading(false);
          setAddUserError(null);
        })
        .catch((error) => {
          setUserSMS(false);
          setIsLoginLoading(false);
          const errorText = error instanceof Error ? error.message : String(error);
          setAddUserError(errorText);
        });
    }
  };

  const resetModal = () => {
    dispatch(closeLoginModal());
    setPhoneIsValid(false);
    setPhoneNumber(undefined);
    setUserExist(false);
    setUserSMS(false);
    setLoginHint(undefined);
    setAddUserError(null);
    setCodeRepeatHidden(true);
    setCodeIsValid(true);
    setIsCodeInputBlocked(false);
    setCodeError('Неизвестная ошибка');
    setBlockTimeLeft(0);
    setAuthKey(undefined);
    setLongError(false);
  };

  const getToken = () => {
    Service.getTokens(authKey!)
      .then((result) => {
        setIsLoginLoading(false);
        const expiresTime = Date.now();
        const expiresInSeconds = Number(result.expires_in);
        const tokenExpirationTime = expiresTime + +expiresInSeconds * 1000;
        localStorage.setItem('refresh_token', String(result.refresh_token));
        localStorage.setItem('tokenExpirationTime', String(tokenExpirationTime));
        localStorage.setItem('tokenLifetime', String(expiresInSeconds));
        localStorage.setItem('access_token', String(result.access_token));
        dispatch(checkAuthStatus());
        resetModal();
      })
      .catch((error) => {
        const errorText = error instanceof Error ? error.message : String(error);
        setAddUserError(errorText);
        setIsLoginLoading(false);
      });
  };

  const handleSetCode = (value: string) => {
    setIsLoginLoading(true);
    setCodeRepeatHidden(true);
    setLongError(false);
    Service.verifyCode(phoneNumber!, value)
      .then((resp) => {
        setAuthKey(resp.key);
        setCodeIsValid(true);
        setIsCodeInputBlocked(false);
        setBlockTimeLeft(0);
        setCodeRepeatHidden(true);
        setIsLoginLoading(false);
        if (blockTimerRef.current) clearInterval(blockTimerRef.current);
      })
      .catch((error) => {
        setIsLoginLoading(false);
        const errorText = error instanceof Error ? error.message : String(error);
        setCodeError(errorText);
        setCodeIsValid(false);
        let blockDuration = 15;
        let repeat = false;
        if (errorText.includes('5 минут')) {
          blockDuration = 300;
          repeat = true;
          setLongError(true);
          setAddUserError(errorText);
        }
        setIsCodeInputBlocked(true);
        setBlockTimeLeft(blockDuration);
        if (blockTimerRef.current) clearInterval(blockTimerRef.current);
        blockTimerRef.current = setInterval(() => {
          setBlockTimeLeft((prev) => {
            if (prev <= 1) {
              setCodeIsValid(true);
              if (!repeat) {
                setIsCodeInputBlocked(false);
              }
              setCodeRepeatHidden(false);
              if (blockTimerRef.current) clearInterval(blockTimerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      });
  };

  const handleSetPhoneNumber = (value: string) => {
    setPhoneNumber(`+7${value}`);
  };

  const handleCloseLogin = () => {
    resetModal();
  };

  const handleAuthLink = () => {
    if (loginHint && userExist) {
      getAuthUrl(loginHint).then((url) => {
        if (typeof url === 'string' && url.trim() !== '') {
          window.location.href = url;
        } else {
          setAddUserError('Не удалось сформировать ссылку для входа');
        }
      });
    }
  };

  const handleCodeLogin = () => {
    sendCode();
  };

  const handleRepeatCode = () => {
    setCodeRepeatHidden(true);
    setIsCodeInputBlocked(false);
    setIsLoginLoading(false);
    setUserSMS(false);
    sendCode();
  };

  useEffect(() => {
    const controller = new AbortController();
    if (phoneNumber) {
      checkPhoneNumber(phoneNumber, controller.signal);
    }
    return () => {
      controller.abort();
    };
  }, [phoneNumber]);

  useEffect(() => {
    checkExist();
  }, [phoneIsValid]);

  useEffect(() => {
    setCodeRepeatHidden(true);
    if (!authKey) {
      return;
    }
    if (userExist) {
      setIsLoginLoading(true);
      Service.updateUserPassword(authKey)
        .then(() => {
          getToken();
        })
        .catch((error) => {
          const errorText = error instanceof Error ? error.message : String(error);
          setAddUserError(errorText);
        });
    } else {
      Service.createUser(authKey)
        .then(() => {
          getToken();
        })
        .catch((error) => {
          const errorText = error instanceof Error ? error.message : String(error);
          setAddUserError(errorText);
        });
    }
  }, [authKey]);

  return (
    <Modal open={isOpenLogin} handleClose={handleCloseLogin}>
      <div className={loginPanel('wrapper')}>
        {userExist && <h3>Вход в кабинет</h3>}
        {!userExist && phoneIsValid && <h3>Регистрация</h3>}
        {isLoginLoading && userSMS && <Loading externalClassName={loginPanel('loading')} />}
        {addUserError && <h3 className={loginPanel('error')}>{addUserError}</h3>}
        <div className={loginPanel('form')}>
          {userSMS && (
            <>
              <p>Введите последние 4 цифры входящего звонка или код из SMS в поле ниже</p>
            </>
          )}
          {userExist && !userSMS && !addUserError && (
            <>
              <p>Войдите по постоянному паролю (если устанавливали), или по коду.</p>
              <p>ВНИМАНИЕ! Вход по коду сбросит постоянный пароль!</p>
            </>
          )}
          {!userSMS && (
            <PhoneInput
              inputId="loginPhoneNumber"
              text="Ваш номер телефона:"
              validate="Номер телефона некорректен"
              isValidExternal={phoneIsValid}
              onComplete={handleSetPhoneNumber}
              valueExternal={phoneNumber}
              externalClassName={loginPanel('phone')}
            />
          )}
          {userSMS && (
            <SmsCode
              length={4}
              onComplete={handleSetCode}
              isValid={codeIsValid}
              disabled={isCodeInputBlocked}
              errorText={
                blockTimeLeft > 0 ? `${codeError} : повтор через ${blockTimeLeft} сек` : codeError
              }
              externalClassName={loginPanel('phone')}
            />
          )}
          <div className={loginPanel('buttons')}>
            {!userSMS && userExist && phoneIsValid && loginHint && (
              <Button text="Войти по паролю" handleExternal={() => handleAuthLink()} />
            )}
            {!userSMS && !longError && userExist && codeRepeatHidden && phoneIsValid && (
              <Button text="Войти по коду" handleExternal={() => handleCodeLogin()} />
            )}
            {!userSMS && !longError && !userExist && codeRepeatHidden && phoneIsValid && (
              <Button text="Зарегистрироваться" handleExternal={() => handleCodeLogin()} />
            )}
            {!codeRepeatHidden && (
              <Button text="Выслать код заново" handleExternal={handleRepeatCode} />
            )}
            <Button text="Отменить" handleExternal={handleCloseLogin} red />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default Login;
